import React from 'react';
import { IContact } from '../../models/Company';
import { useGetContactByIdQuery, useLazyGetContactByIdQuery, useLazyGetContactsQuery } from '../../store/Api';
import { Label, NormalPeoplePicker, IBasePickerSuggestionsProps, IPersonaProps, IPeoplePickerItemSelectedProps, PeoplePickerItem, ValidationState, IPickerItemProps, ISuggestionItemProps, PeoplePickerItemSuggestion, IPeoplePickerItemSuggestionStyles, IPersonaStyles, Persona, Stack } from '@fluentui/react';

interface IContactSearchSelectionProps {
    selectedContactId?: string[];
    onContactSelected?: (contactId: string[]) => void;
    label?: string;
    placeholder?: string;
    getPrimaryText?: (contact: IContact) => string;
    getSecondaryText?: (contact: IContact) => string;
    allowMultiple?: boolean;
}

const ContactSearchSelection: React.FC<IContactSearchSelectionProps> = (props) => {
    const [selectedPersonas, setSelectedPersonas] = React.useState<IPersonaProps[]>([]);
    const [searchContacts] = useLazyGetContactsQuery();
    const [contactResults, setContactResults] = React.useState<IContact[]>([]);
    const [personaCache, setPersonaCache] = React.useState<{[key: string]: IPersonaProps}>({});
    const [getContact] = useLazyGetContactByIdQuery();

    React.useEffect(() => {
        // Preload selected contacts into cache
        const loadSelectedContacts = async () => {
            if (props.selectedContactId) {
                for (const contactId of props.selectedContactId) {
                    if (personaCache[contactId]) continue; // Already cached
                    const response = await getContact({ contactId, includeCompanyDetails: true }).unwrap();
                    setPersonaCache((prev) => ({
                        ...prev,
                        [contactId]: contactToPersona(response)
                    }));
                }
            }
        };
        loadSelectedContacts();
    }, [props.selectedContactId, personaCache]);

    // Helper to map IContact to IPersonaProps, using getSecondaryText if provided
    const contactToPersona = (contact: IContact): IPersonaProps => ({
        key: contact.id || '',
        text: props.getPrimaryText ? props.getPrimaryText(contact) : `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
        secondaryText: props.getSecondaryText ? props.getSecondaryText(contact) : (contact.companyName || contact.emailAddress),
    });

    // Async search for contacts
    const onFilterChanged = async (filterText: string, currentPersonas?: IPersonaProps[]): Promise<IPersonaProps[]> => {
        if (!filterText || filterText.length < 2) return [];
        const response = await searchContacts({ query: { query: filterText }, disabled: false, includeCompanyDetails: true });
        if ('data' in response && response.data?.items) {
            setContactResults(response.data.items);
            const personas = response.data.items.map(contactToPersona);
            // Remove already selected
            return personas.filter(
                (persona) => !currentPersonas?.some((p) => p.key === persona.key)
            );
        }
        return [];
    };

    // Handle selection change
    const onChange = (items?: IPersonaProps[]) => {
        setSelectedPersonas(items || []);
        if (items && items.length > 0 && props.onContactSelected) {
            const selectedIds = items.map(item => item.key?.toString() || '');
            props.onContactSelected(selectedIds);
        } else if (props.onContactSelected) {
            props.onContactSelected([]);
        }
    };

    // Set initial selection if selectedContactId is provided
    React.useEffect(() => {
        if (props.selectedContactId && contactResults.length > 0) {
            const selected = props.selectedContactId.map(contactId => {
                // Try to get from cache first
                if (personaCache[contactId]) {
                    return personaCache[contactId];
                }
                // Fallback to contactResults
                const contact = contactResults.find(c => c.id === contactId);
                if (contact) {
                    return contactToPersona(contact);
                }
                return {
                    key: contactId,
                    text: 'Loading...',
                };
            }).filter((p): p is IPersonaProps => p !== undefined);
            setSelectedPersonas(selected);
        }
    }, [props.selectedContactId, contactResults, personaCache]);

    const onRenderItem = (props: IPickerItemProps<IPersonaProps>) => <PeoplePickerItem
        {...props}
        item={{
            ...props.item,
            ValidationState: ValidationState.valid,
            showSecondaryText: true
        }}
    />

    const suggestionProps: IBasePickerSuggestionsProps = {
        suggestionsHeaderText: 'Suggested contacts',
        noResultsFoundText: 'No contacts found',
    };

    const suggestionItemStyles: Partial<IPeoplePickerItemSuggestionStyles> = {
        subComponentStyles: {
            persona: {
                root: {
                    height: 'auto',
                },
                secondaryText: {
                    height: 'auto',
                    whiteSpace: 'normal',
                },
                primaryText: {
                    height: 'auto',
                    whiteSpace: 'normal',
                },
            }
        },
    };

    const onRenderSuggestionItem = (props: IPersonaProps, itemProps: ISuggestionItemProps<IPersonaProps>) => (
        <PeoplePickerItemSuggestion
            personaProps={props}
            suggestionsProps={suggestionProps}
            styles={suggestionItemStyles}
        />
    )

    return (
        <div>
            {props.label && <Label>{props.label}</Label>}
            <NormalPeoplePicker
                onResolveSuggestions={onFilterChanged}
                resolveDelay={400}
                getTextFromItem={(persona) => persona.text || ''}
                pickerSuggestionsProps={suggestionProps}
                onChange={onChange}
                selectedItems={selectedPersonas}
                inputProps={{ placeholder: props.placeholder || 'Search Contacts' }}
                itemLimit={props.allowMultiple ? 100 : 1}
                onRenderItem={onRenderItem}
                onRenderSuggestionsItem={onRenderSuggestionItem}
            />
            <Stack tokens={{ childrenGap: '0.5em' }} style={{marginTop: '0.5em'}}>
                {props.selectedContactId?.map(id => (
                    <ContactDisplay key={id} contactId={id} getPrimaryText={props.getPrimaryText} getSecondaryText={props.getSecondaryText} />
                ))}
            </Stack>
        </div>
    );
}

interface IContactDisplayProps {
    contactId: string;
    getPrimaryText?: (contact: IContact) => string;
    getSecondaryText?: (contact: IContact) => string;
}

const ContactDisplay: React.FC<IContactDisplayProps> = (props) => {
    const contact = useGetContactByIdQuery({ contactId: props.contactId, includeCompanyDetails: true });
    if (contact.isLoading) return <Persona text='Loading' imageInitials=' ' size={12} showUnknownPersonaCoin />;
    if (contact.isError || !contact.data) return <Persona text='Error loading contact' imageInitials=' ' size={12} showUnknownPersonaCoin />;
    return <Persona
        text={props.getPrimaryText ? props.getPrimaryText(contact.data) : `${contact.data.firstName || ''} ${contact.data.lastName || ''}`.trim()}
        secondaryText={props.getSecondaryText ? props.getSecondaryText(contact.data) : contact.data.companyName || contact.data.emailAddress}
        size={12}
    />;
}

export default ContactSearchSelection;